import * as React from "react";
import { WithStyles, withStyles, createStyles } from 'components/controls/styles';
import { MemberApplicationFormState } from "../entity";
import { isFormState, useApplication } from "../store";
import { useCountries } from "../../countries/store";
import { isEmpty } from "components/utils";
import { Grid, Button, Typography } from "components/controls/widget";
import { formInputProps, sexItems } from "./definition";
import { toDictionary } from "components/state/types";
import { useUIContext } from "../../context/store"
import { useMemberValidation, isMemberValidationState } from "../../member-validation/store";
import { MemberValidationState } from "../../member-validation/entity";
import { TextInput, PhoneInput, ZipCodeInput, NumberInput, Checkbox, DateInput, Combobox, CheckboxSelection } from "components/controls/formControls";

interface Props {
    editedByClub: boolean,
}


const styles = () => createStyles({
    header1: {
        color: '#337ab7',
        fontSize: '1.1rem',
    },
    header2: {
        fontSize: '0.9rem',
    },
    note2: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontSize: '0.75rem',
    },
});


const Phone = ({ editedByClub }: Props) => {
    let [state, actions] = useApplication();
    const [memberValidationState] = useMemberValidation();
    const disableButton = state.loading || memberValidationState.loading;
    const showValidationInfo = !isEmpty(state.entity.member.mobilePhone) && !editedByClub;

    const phoneIsValid = isMemberValidationState(memberValidationState.entity.state, MemberValidationState.PhoneValid)
        && memberValidationState.entity.phone === state.entity.member.mobilePhone;

    const phoneValidationPending = isMemberValidationState(memberValidationState.entity.state, MemberValidationState.PhonePending)
        && memberValidationState.entity.phone === state.entity.member.mobilePhone;

    const phoneValidationExpired = isMemberValidationState(memberValidationState.entity.state, MemberValidationState.PhoneExpired)
        && memberValidationState.entity.phone === state.entity.member.mobilePhone;

    const buttomTitle = phoneValidationExpired ? "Znovu ověřit" : "Ověřit";
    const infoText = phoneValidationPending ? "Ověření probíhá" : "Ověřeno";
    const showInfoText = phoneIsValid || phoneValidationPending;

    return <Grid container direction="row" spacing={16} alignItems="center">
        <Grid item sm={6}>
            <PhoneInput {...formInputProps('member.mobilePhone', state, actions)} />
        </Grid>
        {showValidationInfo && <Grid item sm={6}>
            {showInfoText
                ? <div>{infoText}</div>
                : <Button variant="contained" color="primary" disabled={disableButton} onClick={actions.verifyPhone}>{buttomTitle}</Button>
            }
        </Grid>}
    </Grid>;
}

const HomeAddress = withStyles(styles)(({ classes }: WithStyles<typeof styles>) => {
    let [state, actions] = useApplication();

    return <Grid container direction="column" spacing={8}>
        <Grid item>
            <Typography className={classes.header2} variant="subtitle1">{"Adresa bydliště"}</Typography>
        </Grid>
        <Grid container item spacing={16}>
            <Grid item sm={4}>
                <TextInput  {...formInputProps('member.homeAddress.street', state, actions)} />
            </Grid>
            <Grid item sm={3}>
                <TextInput {...formInputProps('member.homeAddress.city', state, actions)} />
            </Grid>
            <Grid item sm={2}>
                <ZipCodeInput {...formInputProps('member.homeAddress.zipCode', state, actions)} />
            </Grid>
            <Grid item sm={3}>
                <TextInput {...formInputProps('member.homeAddress.post', state, actions)} />
            </Grid>
        </Grid>
    </Grid>;
});

const PostAddress = withStyles(styles)(({ classes }: WithStyles<typeof styles>) => {
    let [state, actions] = useApplication();
    let showAddress = !isFormState(state, MemberApplicationFormState.MemberPostAddressIsSameAsHome);

    return <Grid container direction="column" spacing={8}>
        <Grid item container alignItems="center">
            <Grid item>
                <Typography className={classes.header2} variant="subtitle1">{"Adresa pro korespondenci"}</Typography>
            </Grid>
            <Grid item>
                <Checkbox {...formInputProps('memberPostAddressIsSameAsHome', state, actions)} hideLabel />
            </Grid>
            <Grid>
                <span className={classes.note2}>{'zvolte pokud chcete poštu doručovat jinam než na adresu bydliště'}</span>
            </Grid>
        </Grid>
        {showAddress && <Grid container item spacing={16}>
            <Grid item sm={4}>
                <TextInput  {...formInputProps('member.postAddress.street', state, actions)} />
            </Grid>
            <Grid item sm={3}>
                <TextInput {...formInputProps('member.postAddress.city', state, actions)} />
            </Grid>
            <Grid item sm={2}>
                <ZipCodeInput {...formInputProps('member.postAddress.zipCode', state, actions)} />
            </Grid>
            <Grid item sm={3}>
                <TextInput {...formInputProps('member.postAddress.post', state, actions)} />
            </Grid>
        </Grid>}
    </Grid>;
});

const EmailContextHint = () => {
    let [state, actions] = useApplication();
    const [uiContext] = useUIContext();
    const { logged_user, is_known_email } = uiContext.data;
    const emailChanged = state.previousMemberEmail !== state.entity.member.email;
    const { isKnownEmail, isLoggedWithThisEmail } = is_known_email;

    if (!isKnownEmail) {
        if (logged_user) {
            if (isLoggedWithThisEmail) {
                return <React.Fragment>
                    <Grid item>{'Po dokončení a odeslání přihlášky propojíme váš přihlašovací účet s přihláškou a se záznamem člena.'}</Grid>
                </React.Fragment>;
            } else {
                const checkboxSelectItems = {
                    'A': 'Přihlášení i email v přihlášce jsou moje. Po dokončení a odeslání přihlášky nahraďte email v přihlášení emailem z přihlášky.',
                    'B': 'Přihlašuji někoho jiného, např. své dítě. Po dokončení a odeslání přihlášky založte nový přihlašovací účet a propojte s ním přihlášku i záznam člena.'
                };

                return <React.Fragment>
                    <Grid item>{`Jste přihlášený/á jako ${logged_user.email}, avšak email zde uvedený se liší. Jak máme dále postupovat?`}</Grid>
                    <Grid item>
                        <CheckboxSelection {...formInputProps("accoumtEmailChoose", state, actions)} items={checkboxSelectItems} hideLabel singleSelect />
                    </Grid>
                </React.Fragment>;
            }
        } else {
            return <React.Fragment>
                {emailChanged && <Grid item>{'Při uložení přihlášky vám na tento email odešleme odkaz pro pozdější úpravu přihlášky.'}</Grid>}
                <Grid item>{'Po dokončení a odeslání přihlášky vám pro tento email založíme na webu ČSTS účet. Email použijeme rovněž pro odeslání vašeho prvního přihlašovacího hesla.'}</Grid>
            </React.Fragment>;
        }
    }

    return null;
}


const Email = ({ editedByClub }: Props) => {

    let [state, actions] = useApplication();
    const [memberValidationState] = useMemberValidation();

    const hasEmail = !isEmpty(state.entity.member.email);
    const disableButton = state.loading || memberValidationState.loading;
    const showValidationInfo = !isEmpty(state.entity.member.email);

    const emailIsValid = isMemberValidationState(memberValidationState.entity.state, MemberValidationState.EmailValid)
        && memberValidationState.entity.email === state.entity.member.email;

    const emailValidationPending = isMemberValidationState(memberValidationState.entity.state, MemberValidationState.EmailPending)
        && memberValidationState.entity.email === state.entity.member.email;

    const emailValidationExpired = isMemberValidationState(memberValidationState.entity.state, MemberValidationState.EmailExpired)
        && memberValidationState.entity.email === state.entity.member.email;

    const buttomTitle = emailValidationExpired ? "Znovu ověřit" : "Ověřit";
    const infoText = emailValidationPending ? "Ověření probíhá" : "Ověřeno";
    const showInfoText = emailIsValid || emailValidationPending;
    const showContextHint = hasEmail && !editedByClub;

    return <Grid container spacing={16}>
        <Grid item container direction="column" spacing={8}>
            <Grid item>
                <Grid container spacing={16} alignItems="center">
                    <Grid item sm={8}>
                        <TextInput {...formInputProps("member.email", state, actions)} />
                    </Grid>
                    {showValidationInfo && <Grid item sm={4}>
                        {showInfoText
                            ? <div>{infoText}</div>
                            : <Button variant="contained" color="primary" disabled={disableButton} onClick={actions.verifyEmail}>{buttomTitle}</Button>
                        }
                    </Grid>}
                </Grid>
            </Grid>
        </Grid>

        <Grid item>
            {showContextHint && <EmailContextHint />}
        </Grid>

    </Grid>;
}

const PersonalData = ({ editedByClub }: Props) => {
    let [state, actions] = useApplication();
    let [countriesState] = useCountries();

    const countries = toDictionary(countriesState.entity, c => c.code, c => c.name);
    const { country } = state.entity.member;

    const disablePersonalNumber = country && country !== "CZE";

    return <Grid container spacing={16}>
        <Grid item sm={5}>
            <TextInput {...formInputProps("member.surname", state, actions)} />
        </Grid>
        <Grid item sm={4}>
            <TextInput {...formInputProps('member.name', state, actions)} />
        </Grid>
        <Grid item sm={3}>
            <TextInput  {...formInputProps('member.title', state, actions)} />
        </Grid>


        <Grid item sm={3}>
            <DateInput {...formInputProps('member.birthDate', state, actions)} />
        </Grid>
        <Grid item sm={2}>
            <Combobox  {...formInputProps('member.sex', state, actions)} items={sexItems} />
        </Grid>
        <Grid item sm={3}>
            <PhoneInput {...formInputProps('member.personalIdNumber', state, actions)} disabled={disablePersonalNumber} />
        </Grid>
        <Grid item sm={4}>
            <Combobox {...formInputProps('member.country', state, actions)} items={countries} />
        </Grid>

        <Grid item sm={8}>
            <Email editedByClub={editedByClub} />
        </Grid>

        <Grid item sm={4}>
            <Phone editedByClub={editedByClub} />
        </Grid>

        <Grid item sm={4}>
            <NumberInput {...formInputProps('member.ownedIdt', state, actions)} />
        </Grid>

    </Grid>;
}


const View = withStyles(styles)(({ editedByClub, classes }: Props & WithStyles<typeof styles>) => {

    return <Grid container direction="column" spacing={32}>
        <Grid item>
            <Typography className={classes.header1} variant="h6">{"Údaje člena"}</Typography>
        </Grid>
        <Grid item>
            <PersonalData editedByClub={editedByClub} />
        </Grid>
        <Grid item>
            <HomeAddress />
        </Grid>
        <Grid item>
            <PostAddress />
        </Grid>
    </Grid>
});

export {
    View as MemberView,
}